import { Component, OnInit, Input, EventEmitter, Output, OnDestroy, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Fireworks } from 'fireworks-js';
import { UpdateCheckService } from '@app/shared/services/update-check.service';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'update-modal',
    templateUrl: './update-modal.component.html',
    styleUrls: ['./update-modal.component.scss'],
    host: {
        class: 'absolute inset-0 ',
        style: 'z-index: 999999999999 !important'
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UpdateModalComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() countdownSeconds = 10;
  @Output() countdownFinished = new EventEmitter<void>();
  @Output() deferred = new EventEmitter<void>();

  fireworks: Fireworks;
  private sequenceId = 0;
  private readonly stepDelayMs = 500;
  readonly steps = [
    'SystemAdmin.Components.UpdateModal.Steps.Preparing',
    'SystemAdmin.Components.UpdateModal.Steps.ClearCache',
    'SystemAdmin.Components.UpdateModal.Steps.ApplyUpdate',
    'SystemAdmin.Components.UpdateModal.Steps.Reload'
  ];

  constructor(private updateCheckService: UpdateCheckService) { }

  private readonly stateSubject = new BehaviorSubject<{
    phase: 'countdown' | 'steps' | 'initiating';
    currentCountdown: number | null;
    progressPercent: number;
    currentStepIndex: number;
  }>({
    phase: 'countdown',
    currentCountdown: null,
    progressPercent: 0,
    currentStepIndex: 0
  });

  readonly state$ = this.stateSubject.asObservable();

  private setState(partial: Partial<{
    phase: 'countdown' | 'steps' | 'initiating';
    currentCountdown: number | null;
    progressPercent: number;
    currentStepIndex: number;
  }>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }


  ngOnInit() {
    this.countdownSeconds = Math.max(1, Number(this.countdownSeconds) || 1);
    void this.runSequence();
  }

  initializeFireworks(): void {
    const container = document.querySelector('.fireworks-container');
    this.fireworks = new Fireworks(container, {
      sound: {
        enabled: true,
        files: [
          '/assets/sounds/fx/explosion0.mp3',
          '/assets/sounds/fx/explosion1.mp3',
          '/assets/sounds/fx/explosion2.mp3'
        ],
        volume: {
          min: 12,
          max: 76
        }
      }
    });
    this.fireworks.start();
  }
  
  ngAfterViewInit(): void {
    this.initializeFireworks();
  }

  ngOnDestroy(): void {
    this.cancelSequence();
    if (this.fireworks) {
      this.fireworks.stop();
    }
  }

  deferUpdate(): void {
    this.cancelSequence();
    this.deferred.emit();
  }

  private cancelSequence(): void {
    this.sequenceId += 1;
    this.setState({
      phase: 'countdown',
      currentCountdown: this.countdownSeconds,
      progressPercent: 0,
      currentStepIndex: 0
    });
  }

  private async runSequence(): Promise<void> {
    const sequence = ++this.sequenceId;
    const isCancelled = () => this.sequenceId !== sequence;

    this.setState({
      phase: 'countdown',
      currentCountdown: this.countdownSeconds,
      progressPercent: 0,
      currentStepIndex: 0
    });

    const expectedDuration = (this.countdownSeconds * 1000) + (this.steps.length * this.stepDelayMs) + 200;
    this.updateCheckService.registerUpdateSequenceWindow(expectedDuration);

    for (let remaining = this.countdownSeconds; remaining > 0; remaining -= 1) {
      this.setState({ currentCountdown: remaining });
      await this.sleep(1000);
      if (isCancelled()) {
        return;
      }
    }

    this.setState({ currentCountdown: 0 });
    if (isCancelled()) {
      return;
    }

    this.setState({ phase: 'steps' });
    for (let index = 0; index < this.steps.length; index += 1) {
      this.setState({ currentStepIndex: index });
      this.updateProgressPercentForStep();
      await this.sleep(this.stepDelayMs);
      if (isCancelled()) {
        return;
      }
    }

    this.setState({ phase: 'initiating' });
    if (!isCancelled()) {
      this.countdownFinished.emit();
    }
  }

  private updateProgressPercentForStep(): void {
    const totalSteps = Math.max(1, this.steps.length);
    const currentStepIndex = this.stateSubject.value.currentStepIndex;
    this.setState({
      progressPercent: Math.round(((currentStepIndex + 1) / totalSteps) * 100)
    });
  }

  private sleep(durationMs: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, durationMs));
  }

}
